import styled, { css } from 'styled-components'
import { Container } from 'src/components/common/Container'
import { Typography } from 'src/components/common/Typography'
import {
  Noto_body1_b16,
  Noto_body1_m16,
  Noto_body1_r16,
  Noto_body2_r14,
} from 'src/styles/fonts/NotoSansKrFonts'
import icon_user_plus from 'src/assets/svg/file/icon_user_plus.svg'
import kakao_logo from 'src/assets/images/share/kakao_logo.svg'
import { SmallDivider } from 'src/components/common/Divider'
import React, { useEffect } from 'react'
import { ReactHookFc } from 'src/components/common/ReactHookForm'
import { useFormContext } from 'react-hook-form'
import { FormObjectType, GiftSendFormItemsType } from 'src/types/react-hook-form'
import RenderIf from 'src/components/common/RenderIf'
import { Bridge } from 'src/utils/bridge'
import { useChannel } from 'src/provider/ChannelContextProvider'
import { useModal } from 'src/provider/ModalContextProvider'
import { HyphenReplace } from 'src/utils/regExps_util'

export const GiftSendInfo = ({
  registerAttribute,
}: {
  registerAttribute: Partial<Record<keyof GiftSendFormItemsType, Partial<FormObjectType>>>
}) => {
  const { aos, ios, web } = useChannel()
  const { openModal } = useModal()
  const { recipientPNumber } = registerAttribute
  const {
    formState: { errors },
    watch,
    setValue,
    trigger,
  } = useFormContext<GiftSendFormItemsType>()
  const { recipientPNumber: { message: recipientPNumberErrMsg } = {} } = errors

  const newSendType = watch('sendType')
  const newPNumber = watch('recipientPNumber')

  useEffect(() => {
    if (!(newSendType === 1 && !newPNumber)) trigger('recipientPNumber')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newSendType, newPNumber])

  const openContactPicker = () => {
    if (aos && !Bridge.getContactPermission()) {
      openModal({
        contents: {
          t: '연락처에서 전화번호를 불러올까요?',
          d: '선택한 연락처 외에는 저장하거나 수집하지 않습니다.',
        },
        button: {
          submit: { t: '허용하고 찾기', f: Bridge.handleReqContactPermission },
          cancel: { t: '취소' },
        },
      })
    }
    if ((aos && Bridge.getContactPermission()) || ios) {
      Bridge.handleContactPicker.req()
      Bridge.handleContactPicker.res((pNumber) => {
        if (!!pNumber) setValue('recipientPNumber', HyphenReplace(pNumber))
      })
    }
  }

  return (
    <S.GiftSendInfoContainer $option="flex_column" $row_gap="20px">
      <Typography $fontStyle={Noto_body1_b16} $color="var(--black-90)">
        받는 분
      </Typography>
      <S.GiftSendTypeSelectItem $option="flex_column">
        <S.GiftSendTypeSelectButton
          onClick={() => {
            setValue('sendType', 0)
          }}
        >
          <S.GiftSendTypeRadio $isChecked={newSendType === 0} />
          <S.GiftSendTypeLabel>
            <Typography $fontStyle={Noto_body1_m16}> 카카오톡으로 선물</Typography>
          </S.GiftSendTypeLabel>
        </S.GiftSendTypeSelectButton>
        <RenderIf when={newSendType === 0}>
          <S.GiftSendTypeDetail $option="flex_row" $column_gap="10px">
            <img src={kakao_logo} alt="카카오톡으로 선물" />
            <Typography $fontStyle={Noto_body2_r14} $color="var(--black-90)">
              결제 후 카카오톡 친구 목록에서 선물할 친구를 선택하세요.
            </Typography>
          </S.GiftSendTypeDetail>
        </RenderIf>
      </S.GiftSendTypeSelectItem>
      <SmallDivider />
      <S.GiftSendTypeSelectItem $option="flex_column">
        <S.GiftSendTypeSelectButton
          onClick={() => {
            setValue('sendType', 1)
          }}
        >
          <S.GiftSendTypeRadio $isChecked={newSendType === 1} />
          <S.GiftSendTypeLabel>
            <Typography $fontStyle={Noto_body1_m16}>문자메시지로 선물</Typography>
          </S.GiftSendTypeLabel>
        </S.GiftSendTypeSelectButton>
        <RenderIf when={newSendType === 1}>
          <S.GiftSendTypeDetail>
            <S.GiftPNumberInputWrapper $option="flex_column">
              <Typography $fontStyle={Noto_body2_r14} $color="var(--black-90)">
                받는 분의 정보를 입력해주세요.
              </Typography>
              <S.GiftPNumberInput $option="flex_row" $column_gap="6px">
                <ReactHookFc.Input style={S.FormInput()} {...recipientPNumber} />
                {!web && (
                  <img
                    src={icon_user_plus}
                    alt="연락처에서 찾기"
                    width={20}
                    height={20}
                    onClick={openContactPicker}
                  />
                )}
              </S.GiftPNumberInput>
              <S.FormErrorMsg $text={recipientPNumberErrMsg} />
            </S.GiftPNumberInputWrapper>
          </S.GiftSendTypeDetail>
        </RenderIf>
      </S.GiftSendTypeSelectItem>
    </S.GiftSendInfoContainer>
  )
}

export default GiftSendInfo

const S = {
  GiftSendInfoContainer: styled(Container)`
    padding: 30px 20px 40px;
  `,
  GiftSendTypeSelectItem: styled(Container)``,
  GiftSendTypeSelectButton: styled.section`
    display: flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
  `,
  GiftSendTypeRadio: styled.input.attrs({ type: 'radio' }).attrs({ name: 'send_type' })<{
    $isChecked: boolean
  }>`
    position: relative;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    border: ${({ $isChecked }) => ($isChecked ? 'none' : '1px solid var(--black-20)')};
    background-color: ${({ $isChecked }) => ($isChecked ? 'var(--brand-50)' : 'var(--white)')};
    

    &::after {
      display: ${({ $isChecked }) => ($isChecked ? 'block' : 'none')};
      content: "";
      position: absolute;
      top: 50%;
      left: 50%;
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background-color: var(--white);
      transform: translate(-50%, -50%);
    }

  `,
  GiftSendTypeLabel: styled.label`
    //display: flex;
    cursor: pointer;
  `,
  GiftSendTypeDetail: styled(Container)`
    align-items: center;
    margin-top: 10px;
    padding: 16px;
    border-radius: 20px;
    border: 1px solid var(--brand-50);
    word-break: keep-all;
  `,
  GiftPNumberInputWrapper: styled(Container)``,
  GiftPNumberInput: styled(Container)`
    margin-top: 8px;
    padding: 12px 10px;
    border-radius: 10px;
    background-color: var(--black-4);
  `,
  FormInput: () => css`
    ${Noto_body1_r16};
    color: var(--black-90);

    &::placeholder {
      color: var(--black-40);
    }
  `,
  FormErrorMsg: styled.span<{ $text?: string }>`
    ${Noto_body2_r14};
    margin-top: 6px;
    display: ${(props) => (!!props.$text ? 'flex' : 'none')};
    color: var(--red-50);

    &:before {
      content: '${(props) => props.$text}';
    }
  `,
}
